/**
 * Viewport math for the VTT canvas.
 *
 * The canvas has two coordinate spaces:
 *   - screen space  — CSS pixels relative to the canvas element's top-left
 *   - map space     — the logical, scene-level coordinate system that scene
 *                     elements live in. Unaffected by pan/zoom.
 *
 * The transform from map → screen is:
 *     screen = (map - pan) * zoom
 * and inversely:
 *     map    = screen / zoom + pan
 *
 * `pan` is the map-space point that currently sits at the screen origin
 * (top-left of the canvas). Zooming "at" a screen point keeps that point
 * fixed: we adjust pan so the map-space coord under the cursor is preserved.
 *
 * Viewport is a plain mutable object — no React state, no events. The canvas
 * component owns one and schedules a redraw whenever it mutates it. Keeping
 * it plain keeps pan/zoom at 60fps without the overhead of React rerenders.
 */

export interface Point {
  x: number;
  y: number;
}

/** Clamp zoom so we can't flip or disappear the map. */
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;

export class Viewport {
  /** Map-space point currently at the screen origin. */
  public panX = 0;
  public panY = 0;
  /** Scale factor: 1 screen px = (1/zoom) map units. */
  public zoom = 1;

  /** Convert a screen-space point to map space. */
  screenToMap(sx: number, sy: number): Point {
    return {
      x: sx / this.zoom + this.panX,
      y: sy / this.zoom + this.panY,
    };
  }

  /** Convert a map-space point to screen space. */
  mapToScreen(mx: number, my: number): Point {
    return {
      x: (mx - this.panX) * this.zoom,
      y: (my - this.panY) * this.zoom,
    };
  }

  /** Pan by a delta measured in *screen* pixels (e.g. a mouse drag). */
  panByScreen(dxScreen: number, dyScreen: number): void {
    this.panX -= dxScreen / this.zoom;
    this.panY -= dyScreen / this.zoom;
  }

  /**
   * Zoom in or out by `factor`, keeping the map point under (sx,sy) fixed
   * on screen. This is the standard "zoom toward cursor" behaviour users
   * expect from wheel zoom.
   */
  zoomAt(sx: number, sy: number, factor: number): void {
    const newZoom = clamp(this.zoom * factor, MIN_ZOOM, MAX_ZOOM);
    if (newZoom === this.zoom) return;

    // Map point currently under the cursor — we want it to remain under
    // the cursor after the zoom change.
    const before = this.screenToMap(sx, sy);
    this.zoom = newZoom;
    const after = this.screenToMap(sx, sy);

    // Shift pan so `before` and `after` coincide.
    this.panX += before.x - after.x;
    this.panY += before.y - after.y;
  }

  /**
   * Apply the viewport transform to a 2D rendering context. After this call
   * drawing in map coordinates renders correctly on screen.
   *
   * Callers should wrap their map-space draws in save/restore so the
   * transform doesn't leak into screen-space UI (like the grid labels).
   */
  applyTo(ctx: CanvasRenderingContext2D): void {
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.panX, -this.panY);
  }

  /** Reset to the default view. */
  reset(): void {
    this.panX = 0;
    this.panY = 0;
    this.zoom = 1;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
